import { useApolloClient } from "@apollo/client";
import { ErrorMessage, LoadingSpinner, Modal } from "@msys/ui";
import { Box } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { QuotePreview } from "../../../features/quotes/QuotePreview.js";
import { usePendingDocSharePreviewQuery } from "./PendingDocSharePreviewModal.generated.js";

interface Props {
  pendingDocShareId: string;
  handleClose: () => void;
  // Opens the quick-accept flow (kept stacked on top of the preview so
  // cancelling acceptance leaves the preview open).
  onAccept: () => void;
  // Opens the decline flow; the caller omits it when the viewer may not
  // decline, and no Decline button is shown.
  onDecline?: () => void;
}

// Read-only preview of a pending-shared quote. Reuses the exact `QuotePreview`
// component and `QuotePreview_Quote` fragment used by the project-scoped quote
// preview; the recipient has no project or accepted share yet, so the document
// is loaded through the pending-share-scoped `pendingDocSharePreview` query and
// always mounted with `canEdit={false}`.
export const PendingDocSharePreviewModal = ({
  pendingDocShareId,
  handleClose,
  onAccept,
  onDecline,
}: Props) => {
  const { t } = useTranslate(["Dashboard", "Global"]);
  const client = useApolloClient();

  // `QuotePreview` virtualizes its item rows through `react-virtualized`'s
  // `WindowScroller`, which scrolls whatever element it is handed and falls
  // back to `window` — and `window` never scrolls while a dialog is open. The
  // dialog's own content region is the scroll element, so it has to be handed
  // over; state (not a ref object) because the preview must re-render once the
  // element exists. `contentContainerRef` is typed `React.Ref<unknown>`, hence
  // the `instanceof` narrowing.
  const [contentContainer, setContentContainer] =
    React.useState<HTMLElement | null>(null);
  // Stable identity: an inline callback ref would be detached and re-attached
  // on every render, and setting state from that re-render loops.
  const handleContentContainerRef = React.useCallback((ref: unknown) => {
    setContentContainer(ref instanceof HTMLElement ? ref : null);
  }, []);

  const query = usePendingDocSharePreviewQuery({
    client,
    variables: { id: pendingDocShareId },
    fetchPolicy: "no-cache",
  });

  const preview = query.data?.pendingDocSharePreview;
  const doc =
    preview?.__typename === "PendingDocSharePreviewResult"
      ? preview.quote
      : undefined;
  // Keep deciding behind a successful preview: don't allow accepting or
  // declining while the document is still loading or failed to load.
  const cannotDecide = query.loading || !!query.error || !doc;

  return (
    <Modal
      title={t("Shared document preview", { ns: "Dashboard" })}
      handleClose={handleClose}
      dialogProps={{ fullWidth: true, maxWidth: "md" }}
      // A bounded dialog height gives the content region a stable, measurable
      // scroll viewport from the first render instead of one that grows as the
      // document is measured.
      fixedHeight
      contentContainerRef={handleContentContainerRef}
      actionButtons={[
        {
          label: t("Close", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        ...(onDecline
          ? [
              {
                label: t("Decline", { ns: "Global" }),
                handleClick: onDecline,
                buttonProps: {
                  variant: "outlined" as const,
                  disabled: cannotDecide,
                },
              },
            ]
          : []),
        {
          label: t("Accept", { ns: "Global" }),
          handleClick: onAccept,
          buttonProps: {
            variant: "contained",
            color: "primary",
            disabled: cannotDecide,
          },
        },
      ]}
    >
      {query.loading ? (
        <LoadingSpinner />
      ) : query.error || !doc ? (
        <ErrorMessage
          message={t("The shared document could not be loaded", {
            ns: "Dashboard",
          })}
        />
      ) : (
        <Box>
          <QuotePreview
            projectId={doc.projectId}
            quote={doc}
            container={contentContainer}
            expandedItemIds={undefined}
            hasSapS4HanaIntegration={false}
            canEdit={false}
          />
        </Box>
      )}
    </Modal>
  );
};
